import React, { useState } from "react"
import { Box, ButtonBase, Popover, Stack, Tooltip } from "@mui/material"

import { COLORS, roundNumber } from "../../utils"
import { getRecipePrice } from "../../utils/recipes"

// Up to this number of brands, each brand shows its logo, name and price
const FULL_DISPLAY_MAX_BRANDS = 4
// Beyond it, logos only; past this many logos, the rest goes behind a "+N" button
const COMPACT_DISPLAY_MAX_LOGOS = 10

const isLongBrandName = (brand) => {
    return brand.label.length > 8 && brand.label.includes(" ")
}

const formatPrice = (price) => `${(roundNumber(price, 2) + "").replace(".", ",")}€`

const getBrandPrice = (jsonRecipe, brand) => {
    const price = getRecipePrice(jsonRecipe, brand.name)
    return price || getRecipePrice(jsonRecipe, {})
}

const getBrandTitle = (jsonRecipe, brand) => {
    const price = getBrandPrice(jsonRecipe, brand)
    return price > 0 ? `${brand.label} (${formatPrice(price)})` : brand.label
}

const FullBrands = ({ jsonRecipe, brands }) => (
    <Stack direction="row" gap={3} sx={sx.wrap}>
        {brands.map((brand) => {
            const price = getBrandPrice(jsonRecipe, brand)

            return (
                <Stack key={brand.name} direction={isLongBrandName(brand) ? "row" : "column"}>
                    <Box component="img" sx={sx.logo} src={brand.whiteImage} alt="logo" />
                    <Box>
                        <Box component="span" sx={sx.brandName}>{brand.label}</Box>
                        {price > 0 && <Box component="span" sx={sx.brandName}>{`(${formatPrice(price)})`}</Box>}
                    </Box>
                </Stack>
            )
        })}
    </Stack>
)

const CompactBrands = ({ jsonRecipe, brands }) => {
    const [anchorEl, setAnchorEl] = useState(null)

    const visibleBrands = brands.length > COMPACT_DISPLAY_MAX_LOGOS
        ? brands.slice(0, COMPACT_DISPLAY_MAX_LOGOS - 1)
        : brands
    const hiddenCount = brands.length - visibleBrands.length

    return (
        <>
            <Stack direction="row" alignItems="center" gap={1.5} sx={sx.wrap}>
                {visibleBrands.map((brand) => (
                    <Tooltip key={brand.name} title={getBrandTitle(jsonRecipe, brand)} arrow>
                        <Box component="img" sx={sx.compactLogo} src={brand.whiteImage} alt={brand.label} />
                    </Tooltip>
                ))}
                <ButtonBase sx={sx.moreButton} onClick={(event) => setAnchorEl(event.currentTarget)}>
                    {hiddenCount > 0 ? `+${hiddenCount}` : "Détail"}
                </ButtonBase>
            </Stack>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Stack sx={sx.popoverList} gap={1}>
                    {brands.map((brand) => (
                        <Stack key={brand.name} direction="row" alignItems="center" gap={1}>
                            <Box component="img" sx={sx.logo} src={brand.image} alt="logo" />
                            <Box component="span">{getBrandTitle(jsonRecipe, brand)}</Box>
                        </Stack>
                    ))}
                </Stack>
            </Popover>
        </>
    )
}

const HeaderBrands = ({ jsonRecipe, brands }) => {
    if (brands.length <= FULL_DISPLAY_MAX_BRANDS) {
        return <FullBrands jsonRecipe={jsonRecipe} brands={brands} />
    }

    return <CompactBrands jsonRecipe={jsonRecipe} brands={brands} />
}

const sx = {
    wrap: {
        flexWrap: "wrap",
        rowGap: 1
    },
    logo: {
        width: "16px",
        height: "16px"
    },
    compactLogo: {
        width: "20px",
        height: "20px",
        cursor: "default"
    },
    brandName: {
        fontSize: "16px",
        marginLeft: "8px"
    },
    moreButton: {
        fontSize: "14px",
        padding: "2px 8px",
        borderRadius: "12px",
        border: `1px solid ${COLORS.WHITE}`
    },
    popoverList: {
        padding: "12px 16px"
    }
}

export default HeaderBrands
